import type { RouteSeo } from './types'

/**
 * One blog post, described for search — the one derivation, called from two places.
 *
 * Posts arrive through `virtual:blog-posts`, a bundler concept. The build's metadata
 * emitter runs in Node and cannot resolve it, so `vite.config.ts` reads the markdown
 * directly through `vite/blog-content.ts` while `App.tsx` reads the virtual module. Two
 * readers is unavoidable; two *descriptions* of a post would not be, and would drift the
 * first time a title changed. Hence this file.
 *
 * ## It is loaded by Node, so it may import no component
 *
 * It follows the same rule as an app's `src/data/seo.ts`: **type-only** imports, nothing
 * that reaches a `.tsx`. Every app's `vite.config.ts` imports this file by relative path —
 * `../app_design/src/seo/postSeo` — rather than through `@kkf/design`, because Vite bundles
 * a config with esbuild and runs it in Node, and the barrel re-exports JSX that esbuild
 * cannot parse there. The barrel exports it as well, for `App.tsx`. One derivation, two
 * import paths, and only the second of them is a bundler's.
 *
 * It moved here from `app_website/src/data/blog-seo.ts` on 2026-09-21, when the blog became
 * one library on four sites: four apps describing a post four ways is four descriptions
 * that drift the first time a title changes.
 */

/** What describing a post needs. A fuller post object satisfies it. */
export interface PostSeoInput {
  slug: string
  title: string
  image: string
  /** Absent on a post still carrying a placeholder image. */
  imageAlt?: string
  excerpt: string
  /** Front-matter override, where the excerpt does not work as a search snippet. */
  description?: string
  /** `YYYY-MM-DD`. Absent on an evergreen post, which then carries no dates at all. */
  publishedIso?: string
  category: string
  tags: string[]
}

export const postSeo = (post: PostSeoInput): RouteSeo => {
  const description = post.description ?? post.excerpt
  const image = post.imageAlt ? { url: post.image, alt: post.imageAlt } : undefined

  return {
    path: `/blog/${post.slug}`,
    name: post.title,
    title: post.title,
    description,
    image,
    type: 'article',
    publishedTime: post.publishedIso,
    modifiedTime: post.publishedIso,
    article: {
      headline: post.title,
      description,
      image,
      datePublished: post.publishedIso,
      section: post.category,
      keywords: post.tags,
    },
    // A post is written once and rarely revised; `lastmod` in the sitemap carries the
    // real signal, and a `changefreq` that oversells it is ignored anyway.
    changefreq: 'yearly',
    priority: 0.6,
  }
}
